import type { ResumeData, ResumeSettings, TemplateType } from '../types/resume';
import { ModernTemplate } from '../templates/ModernTemplate';
import { ClassicTemplate } from '../templates/ClassicTemplate';
import { ProfessionalTemplate } from '../templates/ProfessionalTemplate';
import { ExecutiveTemplate } from '../templates/ExecutiveTemplate';
import { MinimalTemplate } from '../templates/MinimalTemplate';
import { TechnicalTemplate } from '../templates/TechnicalTemplate';
import { PortfolioTemplate } from '../templates/PortfolioTemplate';
import { ElegantTemplate } from '../templates/ElegantTemplate';
import { CompactTemplate } from '../templates/CompactTemplate';
import { SidebarRightTemplate } from '../templates/SidebarRightTemplate';
import { AtsTemplate } from '../templates/AtsTemplate';
import { TwoColumnTemplate } from '../templates/TwoColumnTemplate';
import { AcademicTemplate } from '../templates/AcademicTemplate';
import { CreativeTemplate } from '../templates/CreativeTemplate';
import { StandardTemplate } from '../templates/StandardTemplate';
import { PhotoTemplate } from '../templates/PhotoTemplate';
import { CleanTwoColumnTemplate } from '../templates/CleanTwoColumnTemplate';
import { ModernSidebarTemplate } from '../templates/ModernSidebarTemplate';
import {
  AtsCompactTemplate,
  AtsSerifTemplate,
  BoardroomTemplate,
  CloudTemplate,
  CorporateBlueTemplate,
  CybersecurityTemplate,
  DataTemplate,
  DevOpsTemplate,
  EngineerTemplate,
  NetworkTemplate,
} from '../templates/CareerTemplates';

interface Props {
  data: ResumeData;
  template: TemplateType;
  settings?: ResumeSettings;
}

function getPageDimensions(format: ResumeSettings['format'] = 'A4') {
  return format === 'Letter'
    ? { width: '215.9mm', minHeight: '279.4mm' }
    : { width: '210mm', minHeight: '297mm' };
}

export function Preview({ data, template, settings }: Props) {
  const renderTemplate = () => {
    switch (template) {
      case 'standard':
        return <StandardTemplate data={data} settings={settings} />;
      case 'modern':
        return <ModernTemplate data={data} settings={settings} />;
      case 'classic':
        return <ClassicTemplate data={data} settings={settings} />;
      case 'professional':
        return <ProfessionalTemplate data={data} settings={settings} />;
      case 'executive':
        return <ExecutiveTemplate data={data} settings={settings} />;
      case 'minimal':
        return <MinimalTemplate data={data} settings={settings} />;
      case 'technical':
        return <TechnicalTemplate data={data} settings={settings} />;
      case 'portfolio':
        return <PortfolioTemplate data={data} settings={settings} />;
      case 'elegant':
        return <ElegantTemplate data={data} settings={settings} />;
      case 'compact':
        return <CompactTemplate data={data} settings={settings} />;
      case 'sidebar-right':
        return <SidebarRightTemplate data={data} settings={settings} />;
      case 'ats':
        return <AtsTemplate data={data} settings={settings} />;
      case 'two-column':
        return <TwoColumnTemplate data={data} settings={settings} />;
      case 'academic':
        return <AcademicTemplate data={data} settings={settings} />;
      case 'creative':
        return <CreativeTemplate data={data} settings={settings} />;
      case 'ats-compact':
        return <AtsCompactTemplate data={data} settings={settings} />;
      case 'ats-serif':
        return <AtsSerifTemplate data={data} settings={settings} />;
      case 'corporate-blue':
        return <CorporateBlueTemplate data={data} settings={settings} />;
      case 'boardroom':
        return <BoardroomTemplate data={data} settings={settings} />;
      case 'engineer':
        return <EngineerTemplate data={data} settings={settings} />;
      case 'devops':
        return <DevOpsTemplate data={data} settings={settings} />;
      case 'cybersecurity':
        return <CybersecurityTemplate data={data} settings={settings} />;
      case 'network':
        return <NetworkTemplate data={data} settings={settings} />;
      case 'data':
        return <DataTemplate data={data} settings={settings} />;
      case 'cloud':
        return <CloudTemplate data={data} settings={settings} />;
      case 'photo':
        return <PhotoTemplate data={data} settings={settings} />;
      case 'clean-two-column':
        return <CleanTwoColumnTemplate data={data} settings={settings} />;
      case 'modern-sidebar':
        return <ModernSidebarTemplate data={data} settings={settings} />;
      default:
        return <ModernTemplate data={data} settings={settings} />;
    }
  };

  const dims = getPageDimensions(settings?.format);

  const wrapperStyle: React.CSSProperties = {
    width: dims.width,
    minHeight: dims.minHeight,
    background: 'white',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    borderRadius: '2px',
    overflow: 'hidden',
  };

  return (
    <div id="resume-preview" style={wrapperStyle}>
      {renderTemplate()}
    </div>
  );
}
